import {
  lotStatusBreakdownTypeOrder,
  type ClientLotStatusBreakdownSummary,
} from "api-types/locations/locationsResponses";
import { locationLotsPath } from "lib/routeHelpers";
import { useState, type ReactNode } from "react";
import Link from "ui/components/Link";
import ToggleGroup from "ui/forms/ToggleGroup";
import { formatBreakdownValue, formatCount } from "./locationSummaryFormatting";
import { lotStatusLabels, lotTypeLabels } from "./locationSummaryLabels";
import type { BreakdownView, LocationSummaryScope } from "./locationSummaryTypes";
import styles from "./LocationSummaryView.module.scss";

export default function LotBreakdownCard({
  lotSummary,
  scope,
  lotsPossible,
}: {
  lotSummary: ClientLotStatusBreakdownSummary;
  scope: LocationSummaryScope;
  lotsPossible: boolean;
}): ReactNode {
  const [view, setView] = useState<BreakdownView>("count");

  if (!lotsPossible) {
    return null;
  }

  const showViewLots = scope.kind === "location";

  return (
    <section className={styles.card} data-testid="lot-breakdown">
      <div className={styles.cardHeaderRow}>
        <div className={styles.cardHeader}>
          <h3>Lot Breakdown</h3>
          <p>
            {formatCount(lotSummary.totalLots)} total lots by status and type
            {showViewLots && (
              <>
                {" · "}
                <Link href={locationLotsPath(scope.builderId, scope.locationId)} className={styles.inlineLink}>
                  View Lots
                </Link>
              </>
            )}
          </p>
        </div>
        <ToggleGroup.Root
          type="single"
          value={view}
          onValueChange={(value: string) => {
            if (value) {
              setView(value as BreakdownView);
            }
          }}
          allowEmpty={false}
          toggleStyle="secondary"
          className={styles.countToggle}
          aria-label="Lot breakdown display"
        >
          <ToggleGroup.Item value="count" aria-label="Show counts">
            #
          </ToggleGroup.Item>
          <ToggleGroup.Item value="percentage" aria-label="Show percentages">
            %
          </ToggleGroup.Item>
        </ToggleGroup.Root>
      </div>
      {lotSummary.totalLots > 0 ? (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">Lot Status</th>
                {lotStatusBreakdownTypeOrder.map((type) => (
                  <th scope="col" key={type}>
                    {lotTypeLabels[type]}
                  </th>
                ))}
                <th scope="col">Total</th>
              </tr>
            </thead>
            <tbody>
              {lotSummary.rows.map((row) => (
                <tr key={row.key} className={row.key === "TOTAL" ? styles.totalRow : undefined}>
                  <th scope="row">{lotStatusLabels[row.key]}</th>
                  {lotStatusBreakdownTypeOrder.map((type) => (
                    <td key={type} data-zero={row.counts[type] === 0 || undefined}>
                      {formatBreakdownValue(row.counts[type], lotSummary.totalLots, view)}
                    </td>
                  ))}
                  <td data-zero={row.total === 0 || undefined}>
                    {formatBreakdownValue(row.total, lotSummary.totalLots, view)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={styles.emptyState}>
          <strong>No lots yet</strong>
          <p>Add lots to this location to see the lot status breakdown.</p>
        </div>
      )}
    </section>
  );
}
